import { FC, useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { Settings, DragIndicator, BorderVertical, BorderHorizontal } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { uploadImage } from '../../../../../apis/cdn.apis';
import { Dialog as CustomDialog } from '../../../../../components/Dialog';
import { CellConfigDialog } from '../../../../../components';
import tableStyles from '../../../../ManageWidgets/components/Widget/components/Table/styles';
import { dialogStyles, columnSettingsDialogStyles, dragIndicatorStyles, configWrapperStyles, configFieldStyles } from './styles';
import { client } from '../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../queries/widgets';

// Column configuration interface
export interface ColumnConfig {
  type?: 'text' | 'avatar' | 'link';
  showValueConfiguration?: boolean;
  showColumnTooltip?: boolean;
  columnTooltip?: string;
}

// Table data structure matching V1
export interface TableData {
  labels: string[]; // Column headers (same as headers in old structure)
  values: any[][]; // 2D array of cell values (can be strings, objects for links/avatars, or dynamic config objects)
  columns?: ColumnConfig[]; // Column configuration array
}

// Backward compatibility: support old structure { headers, rows }
export interface LegacyTableData {
  headers: string[];
  rows: { label: string; values: string[] }[];
}

// Convert legacy format to new format
function convertLegacyToNew(data: LegacyTableData | TableData): TableData {
  if ('headers' in data && 'rows' in data) {
    // Legacy format
    return {
      labels: data.headers,
      values: data.rows.map(row => row.values),
      columns: new Array(data.headers.length).fill({}).map(() => ({ type: 'text' as const })),
    };
  }
  // Already in new format
  return data as TableData;
}

// Convert new format to legacy format (for backward compatibility)
function convertNewToLegacy(data: TableData): LegacyTableData {
  return {
    headers: data.labels,
    rows: data.values.map((values, index) => ({
      label: `Row ${index + 1}`, // Legacy doesn't have row labels
      values: values.map(v => typeof v === 'string' ? v : (v?.text || v?.linkText || v || '')),
    })),
  };
}

interface TableConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: TableData | LegacyTableData) => void;
  initialData: TableData | LegacyTableData;
  title?: string;
  useLegacyFormat?: boolean; // If true, save in legacy format
  isin?: string; // ISIN for GraphQL queries
}

export const TableConfigurationDialog: FC<TableConfigurationDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  title = 'Configure Table',
  useLegacyFormat = false,
  isin,
}) => {
  const [tableData, setTableData] = useState<TableData>(() => convertLegacyToNew(initialData));
  const [columnSettingIndex, setColumnSettingIndex] = useState<number | null>(null);
  const [selectedPos, setSelectedPos] = useState<[number, number] | null>(null);
  const [selectedCellPos, setSelectedCellPos] = useState<[number, number] | null>(null);
  const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());
  const [loadingCells, setLoadingCells] = useState<Set<string>>(new Set());
  const fileSelector = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const getDynamicSignature = useCallback((config: any): string => {
    try {
      return JSON.stringify(config?.fields || []);
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (open) {
      // Deep copy and convert when dialog opens
      const converted = convertLegacyToNew(initialData);
      setTableData(JSON.parse(JSON.stringify(converted)));
      // Ensure columns array exists
      if (!converted.columns || converted.columns.length !== converted.labels.length) {
        setTableData(prev => ({
          ...prev,
          columns: new Array(prev.labels.length).fill({}).map((_, i) => prev.columns?.[i] || { type: 'text' as const }),
        }));
      }
    }
  }, [open, initialData]);

  const handleAddColumn = useCallback(() => {
    setTableData(prev => ({
      ...prev,
      labels: [...prev.labels, ''],
      values: prev.values.map(row => [...row, '']),
      columns: [...(prev.columns || []), { type: 'text' as const }],
    }));
  }, []);

  const handleRemoveColumn = useCallback((colIndex: number) => {
    if (tableData.labels.length <= 1) return;
    setTableData(prev => ({
      ...prev,
      labels: prev.labels.filter((_, i) => i !== colIndex),
      values: prev.values.map(row => row.filter((_, i) => i !== colIndex)),
      columns: prev.columns?.filter((_, i) => i !== colIndex) || [],
    }));
  }, [tableData.labels.length]);

  const handleAddRow = useCallback(() => {
    setTableData(prev => ({
      ...prev,
      values: [...prev.values, new Array(prev.labels.length).fill('')],
    }));
  }, []);

  const handleRemoveRow = useCallback((rowIndex: number) => {
    if (tableData.values.length <= 1) return;
    setTableData(prev => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== rowIndex),
    }));
  }, [tableData.values.length]);

  const handleChangeLabel = useCallback((columnIndex: number, value: string) => {
    setTableData(prev => ({
      ...prev,
      labels: prev.labels.map((l, i) => i === columnIndex ? value : l),
    }));
  }, []);

  const handleChangeCell = useCallback((rowIndex: number, colIndex: number, value: any, isObject = false) => {
    setTableData(prev => ({
      ...prev,
      values: prev.values.map((row, rIndex) =>
        rIndex === rowIndex
          ? row.map((cell, cIndex) => {
              if (cIndex !== colIndex) return cell;
              if (isObject) {
                return typeof cell === 'object' && cell !== null
                  ? { ...cell, ...value }
                  : value;
              }
              return value;
            })
          : row
      ),
    }));
  }, []);

  const handleOpenColumnSetting = useCallback((columnIndex: number) => {
    setColumnSettingIndex(columnIndex);
  }, []);

  const handleCloseColumnSetting = useCallback(() => {
    setColumnSettingIndex(null);
  }, []);

  const handleChangeColumnType = useCallback((e: any) => {
    const newType = e.target.value;
    setTableData(prev => ({
      ...prev,
      columns: (prev.columns || []).map((col, index) =>
        index === columnSettingIndex
          ? { ...col, type: newType }
          : col
      ),
      // Reset cell values to strings when changing type (except from text)
      values: prev.values.map(row =>
        row.map((cell, cIndex) => {
          if (cIndex === columnSettingIndex) {
            if (newType === 'text') {
              return typeof cell === 'string' ? cell : (cell?.text || cell?.linkText || '');
            } else if (newType === 'link') {
              return typeof cell === 'object' && cell?.linkText !== undefined
                ? cell
                : { linkText: typeof cell === 'string' ? cell : '', linkUrl: '' };
            } else if (newType === 'avatar') {
              return typeof cell === 'string' ? cell : '';
            }
          }
          return cell;
        })
      ),
    }));
  }, [columnSettingIndex]);

  const handleChangeValueConfigOption = useCallback((e: any) => {
    setTableData(prev => ({
      ...prev,
      columns: (prev.columns || []).map((col, index) =>
        index === columnSettingIndex
          ? { ...col, showValueConfiguration: e.target.checked }
          : col
      ),
      values: prev.values.map(row =>
        row.map((cell, cIndex) => {
          if (cIndex === columnSettingIndex) {
            if (e.target.checked) {
              // Turning ON: convert string to dynamic object
              return typeof cell === 'string'
                ? { fields: [{ type: 'text', table: '', field: '', valueType: 'string', text: cell }], isDynamic: true }
                : cell;
            } else {
              // Turning OFF: convert dynamic object back to string
              return typeof cell === 'object' && cell?.isDynamic
                ? cell.fields?.[0]?.text || ''
                : cell;
            }
          }
          return cell;
        })
      ),
    }));
  }, [columnSettingIndex]);

  const handleChangeColumnTooltip = useCallback((e: any) => {
    setTableData(prev => ({
      ...prev,
      columns: (prev.columns || []).map((col, index) =>
        index === columnSettingIndex
          ? { ...col, showColumnTooltip: e.target.checked, columnTooltip: e.target.checked ? (col.columnTooltip || '') : '' }
          : col
      ),
    }));
  }, [columnSettingIndex]);

  const handleChangeColumnTooltipText = useCallback((e: any) => {
    setTableData(prev => ({
      ...prev,
      columns: (prev.columns || []).map((col, index) =>
        index === columnSettingIndex
          ? { ...col, columnTooltip: e.target.value }
          : col
      ),
    }));
  }, [columnSettingIndex]);

  const handleOpenBrowser = useCallback((row: number, col: number) => {
    setSelectedPos([row, col]);
    fileSelector.current?.click();
  }, []);

  const handleUpdateLogo = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedPos || !e.target.files?.[0]) return;
    const [rowIndex, columnIndex] = selectedPos;
    try {
      const res = await uploadImage(e.target.files[0]);
      if (res.status && res.fileName) {
        const imageUrl = res.fileName.startsWith('http') ? res.fileName : `https://cixon-finance-docs.s3.amazonaws.com/${res.fileName}`;
        handleChangeCell(rowIndex, columnIndex, imageUrl);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
    }
    setSelectedPos(null);
    if (fileSelector.current) {
      fileSelector.current.value = '';
    }
  }, [selectedPos, handleChangeCell]);

  const handleOpenCellConfigDialog = useCallback((row: number, col: number) => {
    setSelectedCellPos([row, col]);
  }, []);

  const handleCloseCellConfigDialog = useCallback(() => {
    setSelectedCellPos(null);
  }, []);

  const handleSetCellConfig = useCallback((data: any) => {
    if (selectedCellPos) {
      const [rowIndex, columnIndex] = selectedCellPos;
      setTableData(prev => ({
        ...prev,
        values: prev.values.map((row, rIndex) =>
          rIndex === rowIndex
            ? row.map((cell, cIndex) => (cIndex === columnIndex ? data : cell))
            : row
        ),
      }));
      // Clear cache for this cell since config changed
      const cellKeyPrefix = `${rowIndex}-${columnIndex}-`;
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        Array.from(newCache.keys()).forEach((key) => {
          if (key.startsWith(cellKeyPrefix)) newCache.delete(key);
        });
        return newCache;
      });
    }
    setSelectedCellPos(null);
  }, [selectedCellPos]);

  // Fetch dynamic cell data using GraphQL
  const fetchCellData = useCallback(async (cell: any, rowIndex: number, columnIndex: number) => {
    if (!isin || !cell?.isDynamic || !cell?.fields?.length) return null;
    
    const cellKey = `${rowIndex}-${columnIndex}-${getDynamicSignature(cell)}`;
    
    // Check cache first
    if (cellDataCache.has(cellKey)) {
      return cellDataCache.get(cellKey);
    }

    // Check if already loading
    if (loadingCells.has(cellKey)) {
      return null;
    }

    try {
      setLoadingCells(prev => new Set(prev).add(cellKey));
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [[cell]], isin } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(cellKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch cell data:', error);
      return null;
    } finally {
      setLoadingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  }, [isin, cellDataCache, loadingCells, getDynamicSignature]);

  // Fetch all dynamic cells when dialog opens or data changes
  useEffect(() => {
    if (!open || !isin) return;
    
    const fetchAllCells = async () => {
      const promises: Promise<void>[] = [];
      tableData.values.forEach((row, rowIndex) => {
        row.forEach((cell, columnIndex) => {
          if (cell?.isDynamic && cell?.fields?.length) {
            promises.push(
              fetchCellData(cell, rowIndex, columnIndex).then(() => undefined)
            );
          }
        });
      });
      await Promise.all(promises);
    };
    
    fetchAllCells();
  }, [open, isin, fetchCellData, tableData.values]);

  const onDragEnd = useCallback((result: any) => {
    if (!result.destination) return;
    setTableData(prev => {
      const arr = [...prev.values];
      const removedItem = arr.splice(result.source.index, 1)[0];
      arr.splice(result.destination.index, 0, removedItem);
      return { ...prev, values: arr };
    });
  }, []);

  const handleSave = useCallback(() => {
    const dataToSave = useLegacyFormat ? convertNewToLegacy(tableData) : tableData;
    onSave(dataToSave);
    onClose();
  }, [tableData, useLegacyFormat, onSave, onClose]);

  const currentColumn = columnSettingIndex !== null ? tableData.columns?.[columnSettingIndex] : null;

  return (
    <>
      <CustomDialog open={open} onClose={onClose} sx={dialogStyles}>
        <DialogTitle sx={{ textTransform: 'uppercase' }}>{title.replace(/_/g, ' ')}</DialogTitle>
        <DialogContent sx={{ maxWidth: 'calc(100vw - 330px)' }}>
          <Box display="flex" gap={10}>
            <Button onClick={handleAddColumn}>Add Column</Button>
            <Button onClick={handleAddRow}>Add Row</Button>
          </Box>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={`${title}-table`}>
              {(provided) => (
                <Table sx={{ mt: 10 }} {...provided.droppableProps} ref={provided.innerRef}>
                  <TableHead sx={tableStyles.tableHeader}>
                    <TableRow sx={tableStyles.tableRow}>
                      {tableData.labels.map((label, index) => (
                        <TableCell key={`label-${index}`} sx={tableStyles.tableCell}>
                          <Box display="flex" alignItems="center" gap={8}>
                            <input
                              placeholder="Label"
                              value={label}
                              onChange={(e) => handleChangeLabel(index, e.target.value)}
                            />
                            <Tooltip title="Column Setting">
                              <IconButton sx={{ mr: 4 }} onClick={() => handleOpenColumnSetting(index)}>
                                <Settings />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove Column">
                              <IconButton onClick={() => handleRemoveColumn(index)}>
                                <BorderVertical />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.values?.map((row, rowIndex) => (
                      <Draggable key={rowIndex} draggableId={`${title}-row-${rowIndex}`} index={rowIndex}>
                        {(provided, snapshot) => (
                          <TableRow
                            sx={tableStyles.tableRow}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              background: snapshot.isDragging ? '#ecb6d680' : 'white',
                              ...(snapshot.isDragging && { display: 'flex' }),
                              ...provided.draggableProps.style,
                            }}
                          >
                            {row.map((cell, columnIndex) => {
                              const columnType = tableData.columns?.[columnIndex]?.type || 'text';
                              const isDynamic = tableData.columns?.[columnIndex]?.showValueConfiguration && typeof cell !== 'string' && cell?.isDynamic;
                              return (
                                <TableCell
                                  key={columnIndex}
                                  sx={tableStyles.tableCell}
                                  style={{ ...(snapshot.isDragging && { flex: 1, height: '100%' }) }}
                                >
                                  <Box width="100%" display="flex" alignItems="center">
                                    {columnIndex === 0 && (
                                      <Box sx={dragIndicatorStyles} className="drag-handle" {...provided.dragHandleProps}>
                                        <DragIndicator />
                                      </Box>
                                    )}
                                    {columnType === 'text' && (
                                      <>
                                        {isDynamic ? (
                                          <Box sx={configWrapperStyles}>
                                            {cell.fields?.map((field: any, index: number) => (
                                              <Box key={index} sx={configFieldStyles(theme, field.type)}>
                                                <Typography variant="subtitle2">
                                                  {field.type === 'text'
                                                    ? field.text
                                                    : `${field.table || '(table)'}.${field.field || '(field)'}`}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : (
                                          <input
                                            placeholder="Text"
                                            value={typeof cell === 'string' ? cell : (cell?.text || '')}
                                            onChange={(e) => handleChangeCell(rowIndex, columnIndex, e.target.value)}
                                          />
                                        )}
                                      </>
                                    )}
                                    {columnType === 'link' && (
                                      <>
                                        <input
                                          placeholder="Text"
                                          name="linkText"
                                          value={cell?.linkText || ''}
                                          onChange={(e) => handleChangeCell(rowIndex, columnIndex, { linkText: e.target.value, linkUrl: cell?.linkUrl || '' }, true)}
                                        />
                                        <input
                                          placeholder="URL"
                                          name="linkUrl"
                                          value={cell?.linkUrl || ''}
                                          onChange={(e) => handleChangeCell(rowIndex, columnIndex, { linkText: cell?.linkText || '', linkUrl: e.target.value }, true)}
                                        />
                                      </>
                                    )}
                                    {columnType === 'avatar' && (
                                      <>
                                        {cell && <Avatar sx={{ mr: 10 }} src={cell} />}
                                        <Button size="small" onClick={() => handleOpenBrowser(rowIndex, columnIndex)}>
                                          {cell ? 'Change' : 'Upload'}
                                        </Button>
                                        <input
                                          style={{ display: 'none' }}
                                          type="file"
                                          ref={fileSelector}
                                          onChange={handleUpdateLogo}
                                        />
                                      </>
                                    )}
                                    <Box display="flex" sx={{ ml: 'auto' }}>
                                      {tableData.columns && tableData.columns[columnIndex]?.showValueConfiguration && (
                                        <Tooltip title="Cell Config">
                                          <IconButton onClick={() => handleOpenCellConfigDialog(rowIndex, columnIndex)}>
                                            <Settings />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {columnIndex === 0 && (
                                        <Tooltip title="Remove Row">
                                          <IconButton onClick={() => handleRemoveRow(rowIndex)}>
                                            <BorderHorizontal />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              )}
            </Droppable>
          </DragDropContext>
          <input
            type="file"
            accept="image/*"
            ref={fileSelector}
            style={{ display: 'none' }}
            onChange={handleUpdateLogo}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} color="secondary">Save</Button>
        </DialogActions>
      </CustomDialog>

      {/* Column Settings Dialog */}
      {columnSettingIndex !== null && currentColumn && (
        <CustomDialog open={true} onClose={handleCloseColumnSetting} sx={columnSettingsDialogStyles}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ padding: '0 20px' }}>
              <Box display="flex" alignItems="center">
                <Typography sx={{ mr: 10 }}>Type:</Typography>
                <Select
                  sx={{ flexGrow: 1 }}
                  value={currentColumn.type || 'text'}
                  onChange={handleChangeColumnType}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="avatar">Avatar</MenuItem>
                  <MenuItem value="link">Link</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  label="Show value configuration"
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      checked={currentColumn.showValueConfiguration || false}
                      color="secondary"
                      onChange={handleChangeValueConfigOption}
                    />
                  }
                />
              </Box>
              <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  label="Show Column Tooltip"
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      checked={currentColumn.showColumnTooltip || false}
                      color="secondary"
                      onChange={handleChangeColumnTooltip}
                    />
                  }
                />
                {currentColumn.showColumnTooltip && (
                  <TextField
                    label="Tooltip Text"
                    color="secondary"
                    multiline
                    value={currentColumn.columnTooltip || ''}
                    onChange={handleChangeColumnTooltipText}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseColumnSetting}>Okay</Button>
            </Box>
          </Box>
        </CustomDialog>
      )}

      {/* Cell Config Dialog */}
      {selectedCellPos && (
        <CellConfigDialog
          isin={'test'}
          config={tableData?.values[selectedCellPos[0]]?.[selectedCellPos[1]]}
          open={!!selectedCellPos}
          onClose={handleCloseCellConfigDialog}
          onConfirm={handleSetCellConfig}
        />
      )}
    </>
  );
};
